import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Result, Space, Tooltip } from 'antd';
import { isEqual } from 'lodash';
import { ALIMTALK_SEND_CREATE_URL_PATH } from '../../../../../../constants/service/common/urlConstants';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../../../../../constants/service/common/userPermissions';
import { useAuth } from '../../../../../../hooks/common/useAuth';
import AlimtalkSendHistorySearchBox from '../AlimtalkSendHistorySearchBox';
import AlimtalkSendHistoryTable from '../AlimtalkSendHistoryTable';
import {
  isForbiddenScreenError,
  useAlimtalkSendHistoryListQuery,
  useCancelAlimtalkSendMutation,
} from '../../queries/alimtalkSendHistoryQueries';
import { ALIMTALK_SEND_HISTORY_DEFAULT_PAGE_SIZE } from '../../constants';
import {
  AlimtalkSendHistoryFilter,
  DEFAULT_ALIMTALK_SEND_HISTORY_FILTER,
  getRefetchFailureMessage,
  resolveAutoRefreshInterval,
  resolveQueryFailureView,
  toListQueryParams,
} from '../../utils';
import { AlimtalkSendHistoryListState, parseListSearchParams, serializeListSearchParams } from '../../listSearchParams';

/** 아직 제공되지 않는 기능(다운로드) 버튼 안내 문구 */
const COMING_SOON_TOOLTIP = '준비 중인 기능입니다.';

const AlimtalkSendHistoryListWrap = (): JSX.Element => {
  const navigate = useNavigate();
  const { can } = useAuth();
  // 발송 등록 화면(POST /alimtalk-sends)은 create 권한을 요구한다 — 사이드바 메뉴와 같은 기준
  const canCreate = can(PERMISSION_ACTIONS.CREATE, PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE);

  /**
   *  검색 조건·페이지는 URL 쿼리가 정본이다 — 상세 → 뒤로가기/목록으로 돌아와도 같은 조건·페이지가 복원된다.
   *  잘못된 쿼리값은 parseListSearchParams 가 항목별 기본값으로 떨어뜨린다.
   *  목록 안의 조작(검색·페이지 이동·페이지 보정)은 replace 로 기록해, 뒤로가기가 목록 조작을 한 단계씩 되감지 않고
   *  목록에 들어오기 전 화면으로 간다.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const { filter, page, pageSize } = useMemo(() => parseListSearchParams(searchParams), [searchParams]);

  const writeListState = (next: AlimtalkSendHistoryListState): void => {
    setSearchParams(serializeListSearchParams(next), { replace: true });
  };

  /** 검색 박스 초기값 동기화 키 — 화면 밖(사이드바 메뉴 재진입 등)에서 조건이 바뀌면 검색 박스도 그 조건으로 다시 그린다 */
  const searchBoxKey = new URLSearchParams(
    serializeListSearchParams({ filter, page: 1, pageSize: ALIMTALK_SEND_HISTORY_DEFAULT_PAGE_SIZE }),
  ).toString();

  const params = useMemo(() => toListQueryParams(filter, page, pageSize), [filter, page, pageSize]);

  const { data, isLoading, isFetching, isPreviousData, isError, error, refetch } =
    useAlimtalkSendHistoryListQuery(params);
  const cancelMutation = useCancelAlimtalkSendMutation();

  /**
   *  사용자가 직접 누른 재조회(같은 조건 재검색)만 로딩으로 보여준다.
   *  자동 갱신(refetchInterval)·취소 후 무효화도 isFetching 을 켜므로, isFetching 을 그대로 쓰면 15초마다 표가 깜빡인다.
   */
  const [isManualRefetching, setIsManualRefetching] = useState(false);
  const manualRefetch = (): void => {
    setIsManualRefetching(true);
    refetch().finally(() => setIsManualRefetching(false));
  };

  /**
   *  취소 등으로 totalCount 가 줄어 현재 페이지가 마지막 페이지를 넘으면 마지막 유효 페이지로 보정한다.
   *  (total 0 이면 1페이지) — 보정하지 않으면 빈 페이지가 남고 페이지네이션과 어긋난다.
   *  URL 로 직접 들어온 범위 밖 page 도 같은 경로로 보정된다.
   */
  const totalCount = data?.totalCount;
  useEffect(() => {
    if (totalCount === undefined || isPreviousData) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > lastPage) {
      writeListState({ filter, page: lastPage, pageSize });
    }
    // writeListState 는 매 렌더 새 함수지만 setSearchParams 만 감싼다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, isPreviousData, page, pageSize, filter]);

  /** 조건이 바뀌지 않은 재검색은 query key 가 같아 요청이 나가지 않으므로 직접 refetch 한다 */
  const searchOrRefetch = (nextFilter: AlimtalkSendHistoryFilter): void => {
    if (page === 1 && isEqual(toListQueryParams(nextFilter, 1, pageSize), params)) {
      manualRefetch();
      return;
    }
    writeListState({ filter: nextFilter, page: 1, pageSize });
  };

  const handleSearch = (nextFilter: AlimtalkSendHistoryFilter): void => searchOrRefetch(nextFilter);

  const handleReset = (): void => searchOrRefetch(DEFAULT_ALIMTALK_SEND_HISTORY_FILTER);

  const handleChangePage = (nextPage: number, nextPageSize: number): void => {
    writeListState({ filter, page: nextPage, pageSize: nextPageSize });
  };

  /**
   *  Promise 를 반환해 Popconfirm 이 요청 완료까지 확인 버튼 로딩을 유지하게 한다.
   *  실패 메시지는 mutation onError 가 이미 노출하므로 여기서는 rejection 만 삼킨다(미처리 Promise 방지).
   */
  const handleCancel = (id: number): Promise<unknown> => cancelMutation.mutateAsync(id).catch(() => undefined);

  /**
   *  keepPreviousData 로 남은 이전 조건의 결과(isPreviousData)는 "현재 조건의 데이터"로 치지 않는다 —
   *  조건을 바꾼 조회가 실패하면 이전 조건 결과를 실패한 조건의 결과처럼 보여주지 않고 에러 화면으로 간다.
   */
  const failureView = resolveQueryFailureView({
    isError,
    hasData: data !== undefined && !isPreviousData,
    isForbidden: isForbiddenScreenError(error),
  });

  return (
    <div>
      <AlimtalkSendHistorySearchBox
        key={searchBoxKey}
        defaultFilter={DEFAULT_ALIMTALK_SEND_HISTORY_FILTER}
        initialFilter={filter}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}>
        {canCreate ? (
          <Button type="primary" onClick={() => navigate(ALIMTALK_SEND_CREATE_URL_PATH)}>
            발송 등록
          </Button>
        ) : null}
        <Tooltip title={COMING_SOON_TOOLTIP}>
          <Button disabled>실패 건 다운로드</Button>
        </Tooltip>
        <Tooltip title={COMING_SOON_TOOLTIP}>
          <Button disabled>엑셀 다운로드</Button>
        </Tooltip>
      </Space>

      {/*
        조회 실패는 빈 목록과 구분한다 — 보여줄 현재 조건 데이터가 없으면 테이블 대신 에러 상태를 렌더한다.
        자동 갱신·재조회만 실패했으면 마지막 성공 목록을 유지하고 경고 배너만 띄운다.
        실패 알림은 이 화면(에러 Result 또는 배너) 하나뿐이다 (모달·전역 토스트는 쿼리/API 쪽에서 끈다).
      */}
      {failureView === 'banner' ? (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message={getRefetchFailureMessage(
            resolveAutoRefreshInterval(data?.sends.map(({ status }) => status)) !== false,
          )}
        />
      ) : null}

      {failureView === 'screen' && isForbiddenScreenError(error) ? (
        // 권한 없음(403)은 다시 시도해도 같은 결과라 재시도 버튼을 두지 않는다
        <Result status="403" title="조회 권한이 없습니다." />
      ) : failureView === 'screen' ? (
        <Result
          status="error"
          title="발송 내역을 불러오지 못했습니다."
          subTitle={error?.message}
          extra={
            <Button type="primary" loading={isFetching} onClick={() => refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : (
        <AlimtalkSendHistoryTable
          items={data?.sends ?? []}
          // 로딩은 첫 로드·조건/페이지 변경(isPreviousData)·직접 재조회에만 — 자동 갱신 중에는 띄우지 않는다
          loading={isLoading || isPreviousData || isManualRefetching}
          // URL 에서 읽은 값이라 keepPreviousData 로 이전 응답이 남아 있는 동안에도 클릭한 페이지가 바로 선택된다
          page={page}
          pageSize={pageSize}
          totalCount={data?.totalCount ?? 0}
          onChangePage={handleChangePage}
          onCancel={handleCancel}
          cancelPendingId={cancelMutation.isLoading ? cancelMutation.variables ?? null : null}
        />
      )}
    </div>
  );
};

export default AlimtalkSendHistoryListWrap;
